import CustomPageContainer from '@components/CustomPageContainer';
import { TaggedExportButton, TaggedUploadButton } from '@components/actionsButtons';
import ContentContainer from '@components/contentContainer/ContentContainer';
import { TaggedSelect, TaggedTextInput } from '@components/inputs';
import useDebounce from '@hooks/form';
import useModal from '@hooks/useModal';
import { defaultFilterData } from '@utils/constants';
import {
  Col,
  Form,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useState } from 'react';
import styles from '../../../index.module.less';
import UploadSecondaryMarketTransactionsExcelModal from '../../../modalHouse/modals/secondaryMarket/UploadSecondaryMarketTransactionsExcelModal';
import { exportData, paginationProps } from '../../../utils/tableHelper';
import { useSecondaryMarketTransactionsList, useSecondaryMarketTransactionsListFilterOptions } from '../src/secondaryMarketQueries';

dayjs.extend(localizedFormat);

const SecondaryMarketTransactionsList = () => {
  const { Text } = Typography;

  const [tableFilterData, setTableFilterData] = useState<SecondaryMarket.GetSecondaryMarketTransactionsListParams>(defaultFilterData);
  const [invoiceTermLoanFilter, setInvoiceTermLoanFilter] = useState<any>([
    {
      label: 'Invoice',
      value: 'invoice',
    }, {
      label: 'Term Loan',
      value: 'termLoan',
    },
  ]);

  const {
    showModal: showUploadSecondaryMarketTransactionsExcelModal,
    openModal: openUploadSecondaryMarketTransactionsExcelModal,
    closeModal: closeUploadSecondaryMarketTransactionsExcelModal,
  } = useModal();

  const { data, isFetching, refetch } = useSecondaryMarketTransactionsList(tableFilterData);

  const { data: filterOptions } = useSecondaryMarketTransactionsListFilterOptions();

  const onPaginationChange = (currentPage: number, pageSize?: number) => {
    setTableFilterData((prev) => ({ ...prev, currentPage, pageSize }));
  };

  const onFilterChange = (changedValues: any) => {
    setTableFilterData((prevData) => ({
      ...prevData,
      currentPage: 1,
      ...changedValues,
    }));
  };

  useDebounce(tableFilterData.search, refetch);

  const handleExportTable = () => {
    const formattedData = data?.data.map(({ isInvoice, ...item }) => ({
      investmentNoteType: isInvoice ? 'Invoice' : 'Term Loan',
      investmentNoteId: item.investmentNoteId,
      investmentNoteName: item.investmentNoteName,
      sellerId: item.sellerId,
      sellerName: item.sellerName,
      sellDate: item.sellDate,
      buyerId: item.buyerId,
      buyerName: item.buyerName,
      buyDate: item.buyDate,
      outstandingInvestedAmount: item.outstandingInvestedAmount,
      premiumDiscount: item.premiumDiscount,
      resellAmount: item.basePrice,
      sellerGrossFeesAndCharges: item.sellerGrossFeesAndCharges,
      sellerFeesAndChargesGovTax: item.sellerFeesAndChargesGovTax,
      sellerFeesAndCharges: item.sellerFeesAndCharges,
      netResellAmount: item.netResellAmount,
      purchaseAmount: item.basePrice,
      buyerGrossFeesAndCharges: item.buyerGrossFeesAndCharges,
      buyerFeesAndChargesGovTax: item.buyerFeesAndChargesGovTax,
      buyerFeesAndCharges: item.buyerFeesAndCharges,
      netPurchaseAmount: item.netPurchaseAmount,
      status: item.status,
    }));

    exportData(formattedData, `secondary_market_transactions_${dayjs().format('L_LT')}`);
  };

  const determineTagColor = (value: string) => {
    if (value === 'Listed') {
      return 'processing';
    }

    if (value.includes('Delisted')) {
      return 'error';
    }

    return 'success';
  };

  const columns: ColumnsType<SecondaryMarket.SecondaryMarketTransactionData> = [
    { title: 'ID', dataIndex: 'id', align: 'center' },
    {
      title: 'Note Type',
      dataIndex: 'isInvoice',
      align: 'center',
      render: (value) => (
        <Tag key={value} color={value ? 'green' : 'orange'}>
          {value ? 'Invoice' : 'Term Loan'}
        </Tag>
      ),
    },
    { title: 'Investment Note ID', dataIndex: 'investmentNoteId', align: 'center' },
    { title: 'Investment Note Name', dataIndex: 'investmentNoteName', align: 'center' },
    { title: 'Seller ID', dataIndex: 'sellerId', align: 'center' },
    { title: 'Seller Name', dataIndex: 'sellerName', align: 'center' },
    {
      title: 'Sell Date', dataIndex: 'sellDate', align: 'center', ellipsis: true,
    },
    { title: 'Buyer ID', dataIndex: 'buyerId', align: 'center' },
    { title: 'Buyer Name', dataIndex: 'buyerName', align: 'center' },
    {
      title: 'Buy Date', dataIndex: 'buyDate', align: 'center', ellipsis: true,
    },
    { title: 'Outstanding Invested Amount (RM)', dataIndex: 'outstandingInvestedAmount', align: 'right' },
    {
      title: 'Premium/Discount (RM)',
      dataIndex: 'premiumDiscount',
      align: 'right',
      render: (value) => {
        const floatValue = parseFloat(value);

        if (floatValue > 0) {
          return (
            <Text type="success">
              +
              {value}
            </Text>
          );
        }

        if (floatValue < 0) {
          return <Text type="danger">{value}</Text>;
        }

        return <Text>{value}</Text>;
      },
    },
    { title: 'Resell Amount (RM)', dataIndex: 'basePrice', align: 'right' },
    { title: 'Seller Fees and Charges (RM)', dataIndex: 'sellerGrossFeesAndCharges', align: 'right' },
    { title: 'Seller SST (RM)', dataIndex: 'sellerFeesAndChargesGovTax', align: 'right' },
    { title: 'Seller Fees and Charges incl. SST (RM)', dataIndex: 'sellerFeesAndCharges', align: 'right' },
    { title: 'Net Resell Amount (RM)', dataIndex: 'netResellAmount', align: 'right' },
    { title: 'Purchase Amount (RM)', dataIndex: 'basePrice', align: 'right' },
    { title: 'Buyer Fees and Charges (RM)', dataIndex: 'buyerGrossFeesAndCharges', align: 'right' },
    { title: 'Buyer SST (RM)', dataIndex: 'buyerFeesAndChargesGovTax', align: 'right' },
    { title: 'Buyer Fees and Charges incl. SST (RM)', dataIndex: 'buyerFeesAndCharges', align: 'right' },
    { title: 'Net Purchase Price (RM)', dataIndex: 'netPurchaseAmount', align: 'right' },
    {
      title: 'Status', dataIndex: 'status', align: 'center', render: (value) => <Tag color={determineTagColor(value)}>{value}</Tag>,
    },
  ];

  return (
    <CustomPageContainer breadcrumbName="Secondary Market Transactions" title="Secondary Market Transactions">
      <ContentContainer>
        <Form
          layout="vertical"
          onValuesChange={onFilterChange}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Filter by Note ID/Note Name/Seller ID/Seller Name/Buyer ID/Buyer Name" name="search">
                <TaggedTextInput tag="search" placeholder="Enter Note ID/Note Name/Seller ID/Seller Name/Buyer ID/Buyer Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Filter by Invoice/Term Loan" name="invoiceTermLoanFilter">
                <TaggedSelect
                  tag="invoiceTermLoanFilter"
                  placeholder="Select Invoice/Term Loan"
                  options={invoiceTermLoanFilter}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Filter by Status" name="statusFilter">
                <TaggedSelect
                  tag="statusFilter"
                  placeholder="Select Status"
                  options={filterOptions?.transactionStatusFilter || []}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Space direction="vertical" size="large" className={styles['full-width']}>
          <Row justify="end" gutter={4}>
            <Col>
              <TaggedExportButton tag="export" onClick={handleExportTable}>
                Export
              </TaggedExportButton>
            </Col>
            <Col>
              <TaggedUploadButton
                tag="uploadMultipleTransactions"
                onClick={openUploadSecondaryMarketTransactionsExcelModal}
              >
                Upload for Multiple Transactions
              </TaggedUploadButton>
            </Col>
          </Row>
          <Table<any>
            columns={columns}
            dataSource={data?.data}
            rowKey="id"
            rowClassName={(record, index) => (index % 2 === 0 ? 'dark-stripe' : '')}
            loading={isFetching}
            scroll={{
              x: true,
            }}
            pagination={{
              ...paginationProps(data?.totalFiltered),
              onChange: onPaginationChange,
              current: tableFilterData.currentPage,
              hideOnSinglePage: true,
            }}
          />
        </Space>
      </ContentContainer>
      <UploadSecondaryMarketTransactionsExcelModal
        closeModal={closeUploadSecondaryMarketTransactionsExcelModal}
        showModal={showUploadSecondaryMarketTransactionsExcelModal}
      />
    </CustomPageContainer>
  );
};

export default SecondaryMarketTransactionsList;
